// Rule-inspector trace helpers (see Docs/rule-inspector.txt).
// Turns the engine's RLE-by-change decision trace into per-node paint state for
// the read-only tactic render. Pure functions, no React — unit tested.
//
// Trace shape (autotest response, keyed by LOCAL player index as a string):
//   { "<player>": [ { from, visited: [[tokenOffset, 0|1], ...], fired }, ... ] }
// RLE by change: an entry holds FROM its frame until the next entry's frame.

/** The decision entry active for `player` at `frame` (largest from <= frame), or null. */
export function frameDecision(trace, player, frame) {
  const timeline = trace?.[String(player)];
  if (!timeline || !timeline.length) return null;
  let chosen = null;
  for (const entry of timeline) {          // entries are emitted in ascending `from`
    if (entry.from <= frame) chosen = entry;
    else break;
  }
  return chosen;
}

/** Normalize a decision into lookup structures for the renderer. */
// Trace visited tuples are [tacticId, offset, value] and `fired` belongs to
// `firedTactic` (tacticId -1 = the player's own tactic; >=0 = a called tactic —
// tactic calls, Docs/tactic-calls.txt). `tacticId` scopes the paint state to one
// tactic: default -1 paints the player's own tactic; pass a callee id to paint
// the called tactic when navigating into it.
export function decisionState(decision, tacticId = -1) {
  const visited = new Map();
  const calls = new Set();           // callee ids this decision stepped into (CALL_TACTIC)
  if (decision && decision.visited) {
    for (const v of decision.visited) {
      if (v[0] === tacticId) visited.set(v[1], v[2] === 1);
      if (v[0] >= 0) calls.add(v[0]);
    }
  }
  if (decision && decision.firedTactic >= 0) calls.add(decision.firedTactic);
  const fired = (decision && decision.firedTactic === tacticId) ? decision.fired : -1;
  return { has: !!decision, visited, fired, calls };
}

/** Convenience: state for `player` at `frame` straight from the raw trace. */
export function frameState(trace, player, frame, tacticId = -1) {
  return decisionState(frameDecision(trace, player, frame), tacticId);
}

/** Paint class for a query node (atom or composite) at `offset`. */
export function queryClass(offset, state) {
  if (!state.visited.has(offset)) return "insp-gray";      // short-circuited / not reached
  return state.visited.get(offset) ? "insp-true" : "insp-false";
}

/** Paint class for an action node at `offset`. */
export function actionClass(offset, state) {
  return offset === state.fired ? "insp-fired" : "insp-idle";
}

/**
 * Which branch of an IF was taken this frame, from its condition's value:
 *   "then" | "else" | null (condition not visited -> nothing emphasized).
 */
export function branchTaken(condition, state) {
  if (!condition || !state.visited.has(condition.tokenOffset)) return null;
  return state.visited.get(condition.tokenOffset) ? "then" : "else";
}

// ── Slow-mode narration (Fase 4) ─────────────────────────────────────────────
// A decision is narrated step by step. Stepping INTO a called tactic is its OWN
// step (the @call line lights up) BEFORE the callee's first condition — so a
// novice sees the jump and the evaluation as two separate moments.

/**
 * The ordered narration steps for a decision. Each step is one of:
 *   { type: "enter", tacticId }  — jump into a called tactic (highlight @call)
 *   { type: "cond",  i }         — reveal visited[i] (a condition result)
 *   { type: "action" }           — reveal the fired action (last)
 * An "enter" is inserted whenever the visited sequence crosses into a new
 * callee tactic (tacticId >= 0), before that callee's first condition.
 */
function narrationPlan(decision) {
  const plan = [];
  const all = decision?.visited || [];
  let prevTactic = -1; // narration starts in the player's own tactic
  for (let i = 0; i < all.length; i++) {
    const tid = all[i][0];
    if (tid >= 0 && tid !== prevTactic) plan.push({ type: "enter", tacticId: tid });
    plan.push({ type: "cond", i });
    prevTactic = tid;
  }
  plan.push({ type: "action" });
  return plan;
}

/** Number of narration steps for a decision (conditions + call-entries + action). */
export function narrationSteps(decision) {
  return decision ? narrationPlan(decision).length : 0;
}

/**
 * A decisionState revealing only the first `count` narration steps (see
 * narrationPlan). `calls` holds the callees the narration has stepped into so
 * far (an "enter" step adds the callee BEFORE its first condition is colored,
 * so the @call line and the first callee condition light up on separate steps).
 */
export function partialState(decision, count, tacticId = -1) {
  const visited = new Map();
  const calls = new Set();
  let fired = -1;
  if (decision) {
    const all = decision.visited || [];            // tuples [tacticId, offset, value]
    const plan = narrationPlan(decision);
    const shown = Math.max(0, Math.min(count, plan.length));
    for (let i = 0; i < shown; i++) {
      const step = plan[i];
      if (step.type === "enter") {
        calls.add(step.tacticId);
      } else if (step.type === "cond") {
        const v = all[step.i];
        if (v[0] === tacticId) visited.set(v[1], v[2] === 1);
        if (v[0] >= 0) calls.add(v[0]);
      } else if (step.type === "action") {
        if (decision.firedTactic === tacticId) fired = decision.fired;
        if (decision.firedTactic >= 0) calls.add(decision.firedTactic);
      }
    }
  }
  return { has: !!decision, visited, fired, calls };
}

/** Frames where `player`'s decision CHANGES (the RLE entry `from` values). */
export function changeFrames(trace, player) {
  const timeline = trace ? trace[String(player)] : null;
  if (!timeline) return [];
  return timeline.map((e) => e.from);
}
